import { mountSimpleTable } from './mountSimpleTable';
import { Accordion, IAccordionItemProperties } from '@vfde-brix/accordion';
import mountAccordion from './mountAccordion';
import { HEADLINE_DELIVERY_SCOPE, TECHNICAL_DETAILS_ACCORDION } from '../container/DeviceDetail/constants';
import mountSimpleList from './mountSimpleList';
import { MAP_VLUX_ID_TO_ICON } from '../container/Options/constants';
import { ICON_BUNDLES } from '@vfde-brix/core';
import { StateProps } from 'app/container/Options/interfaces/state';

let cmsItemIds: string[];

/**
 * Technical Details component
 *
 * @param technicalDetails
 * @param deliveryScope
 * @param isFirstCall Boolean to identify the first accordion mount
 * so we can store the CMS accordion item IDs
 * @constructor
 */

export const mountTechnicalDetailsAccordion = (
    technicalDetails: StateProps['technicalDetails'],
    deliveryScope: string[],
    isFirstCall: boolean,
): Accordion | null => {
    const container = document.getElementById(TECHNICAL_DETAILS_ACCORDION);

    if (!container) {
        return null;
    }

    const tmpContainer = document.createElement('div');
    const accordion = mountAccordion(container);
    const items = [
        ...createTechnicalDetailsData(technicalDetails),
        createDeliveryScopeData(deliveryScope),
    ];
    const tmpAccordion = mountAccordion(tmpContainer, {items, business: {}});
    const cmsItems = getItemsFromCMS(accordion, isFirstCall);

    if (isFirstCall) {
        cmsItemIds = cmsItems.map(item => item.stdId);
    }

    accordion.update({
        items: [
            ...cmsItems,
            ...tmpAccordion.getPropertyByKey('items'),
        ],
        optOpenIndividually: true,
    });

    return accordion;
};

/**
 * Reads the editorial item properties from the given accordion
 * and returns them as array. When this function is called
 * the first time `isFirstCall` must be set to true, so that
 * all items can be returned. On any following call `isFirstCall`
 * should be false so that the items will be compared to the
 * `cmsItemIds` array which contains the IDs of accordion items
 * coming from the CMS.
 * @param accordion The accordion component object
 * @param isFirstCall Boolean
 * @returns Array of accordion item properties
 */
const getItemsFromCMS = (accordion: Accordion, isFirstCall: boolean): IAccordionItemProperties[] => {
    const cmsItems: IAccordionItemProperties[] = accordion.getPropertyByKey('items');
    const items: IAccordionItemProperties[]  = [];

    for (const cmsItem of cmsItems) {
        const isWhitelisted = isFirstCall || cmsItemIds.indexOf(cmsItem.stdId) !== -1;

        if (isWhitelisted) {
            const wrapper = document.getElementById(`accordion-item__content-${cmsItem.stdId}`);
            cmsItem.containerAnyComponent = wrapper?.getElementsByClassName('brix-accordion-item__content-box')[0].innerHTML;

            // TODO: PatternLib: containerAnyComponent needs readed in readDom

            if (cmsItem.containerAnyComponent?.trim().length > 0) {
                items.push(cmsItem);
            }
        }
    }

    return items;
};

const createTechnicalDetailsData = (technicalDetails: StateProps['technicalDetails']) => {
    const data = [];
    let i = 0;

    for (const [sectionLabel, section] of Object.entries(technicalDetails!)) {
        const simpleTableContainer = document.createElement('div');

        mountSimpleTable(simpleTableContainer, (<any>section).attributes);

        data.push({
            stdId: sectionLabel.replace(' ', '-') + i,
            stdHeadline: sectionLabel,
            containerAnyComponent: simpleTableContainer.innerHTML,
            optIconLeft: MAP_VLUX_ID_TO_ICON[section.id as keyof typeof MAP_VLUX_ID_TO_ICON],
        });

        i++;
    }

    return data;
};

const createDeliveryScopeData = (deliveryScope: string[]) => {
    const deliveryScopeElement = document.createElement('div');

    mountSimpleList(deliveryScopeElement, deliveryScope);

    return {
        stdId: HEADLINE_DELIVERY_SCOPE.replace(' ', '-') + '_vf',
        stdHeadline: HEADLINE_DELIVERY_SCOPE,
        containerAnyComponent: deliveryScopeElement.innerHTML,
        optIconLeft: ICON_BUNDLES,
    };
};
